/**
 * 링크 정본 — 온보딩 화면의 모든 링크가 여기서 나온다.
 *
 * 출처는 워크스페이스 실측이다: 서비스 주소는 각 repo의 `.env*`·ingress·CORS 허용 목록, 관측 도구는
 * 알림/대시보드 설정(`grafana-*`·`argocd-*`·runbook URL), GCP는 배포 스크립트의 프로젝트 ID.
 * 주소가 바뀌거나 열리지 않으면 화면이 아니라 **이 파일**을 고친다.
 */
export type Env = 'prod' | 'dev' | 'both';

export interface DevLink {
  label: string;
  url: string;
  /** 이 링크가 무엇인지 — 처음 보는 사람이 열어볼지 말지 판단할 한 줄. */
  note: string;
  env?: Env;
  /** 열리지 않을 때 무엇을 요청해야 하는가. 계정 발급 체크리스트와 같은 말을 쓴다. */
  access?: string;
  /** 시작하기 화면의 "매일 여는 것"에 올릴 링크. 늘리면 그 화면이 링크 모음과 같아진다. */
  essential?: boolean;
}

export interface LinkGroup {
  key: string;
  title: string;
  hint: string;
  links: DevLink[];
}

export const LINK_GROUPS: LinkGroup[] = [
  {
    key: 'service',
    title: '🌐 서비스 웹',
    hint: '우리가 만드는 화면입니다 — dev에서 먼저 확인하고, prod는 눈으로만 봐 주세요',
    links: [
      { label: '소매·도매 웹 (vue_retail)', url: 'https://ceo.ndmarket.co.kr', env: 'prod', note: '메인 커머스 웹입니다. 로그인 후 도매/소매 모드가 갈립니다' },
      { label: '소매·도매 웹 — dev', url: 'https://dev-ceo.ndmarket.co.kr', env: 'dev', essential: true, note: '개발 서버입니다. 기능 확인은 여기서 해 주세요' },
      { label: '모바일 웹', url: 'https://m.ndmarket.co.kr', env: 'prod', note: 'vue_retail의 `/m/*`입니다. 하이브리드 앱(market-rn)이 이 주소를 WebView로 감쌉니다' },
      { label: '어드민 (vue_admin)', url: 'https://admin.ndmarket.co.kr', env: 'prod', note: '운영팀이 쓰는 관리자 화면입니다. 상품·주문·광고·정산을 다룹니다', access: '어드민 계정' },
      { label: '남도몰 (ndmall-nextjs)', url: 'https://www.ndmarket.co.kr', env: 'prod', note: '수출용 다국어 스토어 프론트입니다' },
      { label: 'QR 랜딩', url: 'https://qrcode.ndmarket.co.kr', env: 'prod', note: '오프라인 QR 유입 랜딩 페이지입니다' },
      { label: 'UNCLE POS', url: 'https://pos.ndmarket.co.kr', env: 'prod', note: '별개 사업(엉클)입니다 — 남도마켓과는 교차 연동만 있습니다' },
    ],
  },
  {
    key: 'api',
    title: '🔗 API 엔드포인트',
    hint: '프런트가 부르는 주소입니다. 로컬 개발에서 이 값을 `.env`에 넣어 주세요',
    links: [
      { label: 'nd-market-api — prod', url: 'https://apis.ndmarket.co.kr', env: 'prod', note: '메인 B2B 도매/소매 API입니다' },
      { label: 'nd-market-api — dev', url: 'https://apis-dev.ndmarket.co.kr', env: 'dev', essential: true, note: '로컬 프런트가 붙는 기본 백엔드입니다' },
      { label: 'nd-market-api — Swagger', url: 'https://apis-dev.ndmarket.co.kr/api-docs', env: 'dev', essential: true, note: 'API 문서입니다. 스펙 정본은 docs/components/*.yaml에 있습니다', access: '관리자 계정(basic auth)' },
      { label: 'nd-welfare-api — prod', url: 'https://apis.careonplus.com', env: 'prod', note: '복지몰(careonplus)입니다. DB·도메인이 남도마켓과 분리돼 있습니다' },
      { label: 'nd-welfare-api — dev', url: 'https://apis-dev.careonplus.com', env: 'dev', note: '복지몰 개발 서버입니다' },
      { label: '펭디 챗봇 API', url: 'https://api.pengdi.ndmarket.co.kr', env: 'prod', note: 'AI 쇼핑도우미입니다' },
      { label: 'CDN', url: 'https://cdn.ndmarket.co.kr', env: 'prod', note: '상품 이미지·정적 자산입니다. dev는 cdn-dev를 씁니다' },
    ],
  },
  {
    key: 'observability',
    title: '📈 관측 · 운영',
    hint: '장애가 나면 여기부터 봐 주세요 — 지표(Grafana) → 로그(Kibana) → 예외(Sentry) 순서입니다',
    links: [
      { label: 'Grafana', url: 'https://grafana.ndmarket.co.kr', env: 'both', essential: true, note: '지표·알림 정본입니다. 알림 메시지의 링크가 여기로 옵니다', access: 'Grafana 계정' },
      { label: 'Grafana — ND 앱 지표', url: 'https://grafana.ndmarket.co.kr/d/nd-apps-dashboard-prod', env: 'prod', note: '서비스 상태·파드 CPU/메모리·재시작 수를 봅니다' },
      { label: 'Grafana — 소켓·RMQ 운영', url: 'https://grafana.ndmarket.co.kr/d/socket-rmq-operations', env: 'prod', note: '소켓 연결·RabbitMQ 큐를 봅니다' },
      { label: 'Kibana', url: 'https://kibana.ndmarket.co.kr', env: 'prod', note: '애플리케이션 로그를 검색합니다', access: 'Kibana 계정' },
      { label: 'Sentry', url: 'https://345067d2d3aa.sentry.io', env: 'both', essential: true, note: '런타임 예외 추적입니다. 조직 초대를 받아야 프로젝트가 보입니다', access: 'Sentry 조직 초대' },
      { label: 'ArgoCD — prod', url: 'https://argocd.ndmarket.co.kr', env: 'prod', essential: true, note: 'GKE 배포 상태(GitOps)입니다. 매니페스트 정본은 nd-k8s-manifests에 있습니다', access: 'ArgoCD 계정' },
      { label: 'ArgoCD — dev', url: 'https://argocd-dev.ndmarket.co.kr', env: 'dev', note: '개발 클러스터 배포 상태입니다', access: 'ArgoCD 계정' },
    ],
  },
  {
    key: 'cloud',
    title: '☁️ GCP 콘솔',
    hint: 'GKE는 ndmarket-prod·ndmarket-dev, DB와 사내 도구는 namdomarket-dacfe입니다 — 프로젝트를 먼저 확인하고 눌러 주세요',
    links: [
      { label: 'GKE 워크로드 — prod', url: 'https://console.cloud.google.com/kubernetes/workload?project=ndmarket-prod', env: 'prod', note: '파드 상태·로그(nd-prod-cluster-v2)를 봅니다. 배포 자체는 ArgoCD가 합니다', access: 'GCP IAM' },
      { label: 'GKE 워크로드 — dev', url: 'https://console.cloud.google.com/kubernetes/workload?project=ndmarket-dev', env: 'dev', note: '개발 클러스터(nd-dev-cluster)입니다', access: 'GCP IAM' },
      { label: 'Cloud Run — 사내 도구', url: 'https://console.cloud.google.com/run?project=namdomarket-dacfe', env: 'both', note: '개발자 콘솔·AI 플랫폼 등 사내 서비스가 있습니다', access: 'GCP IAM' },
      { label: 'Cloud Run — prod', url: 'https://console.cloud.google.com/run?project=ndmarket-prod', env: 'prod', note: 'GKE 밖에서 도는 서비스(배송 추적·자동화 등)가 있습니다', access: 'GCP IAM' },
      { label: 'Cloud SQL', url: 'https://console.cloud.google.com/sql/instances?project=namdomarket-dacfe', env: 'both', note: 'MySQL 인스턴스(nd-database·nd-database-dev)입니다. 접속은 cloud-sql-proxy로만 해 주세요', access: 'GCP IAM' },
      { label: 'Secret Manager', url: 'https://console.cloud.google.com/security/secret-manager?project=ndmarket-prod', env: 'prod', note: '환경변수 정본입니다. `.env`는 저장소에 커밋하지 않습니다', access: 'GCP IAM' },
      { label: 'Cloud Logging', url: 'https://console.cloud.google.com/logs/query?project=ndmarket-prod', env: 'prod', note: 'Kibana에 없는 인프라 레벨 로그를 봅니다', access: 'GCP IAM' },
    ],
  },
  {
    key: 'build',
    title: '📱 앱 빌드',
    hint: '웹은 Vercel·ArgoCD가 배포합니다 — 여기 있는 것은 네이티브 앱 빌드용 내부 도구입니다',
    links: [
      { label: '앱스토어 자동화', url: 'https://appstore-automation.vercel.app/', note: '앱 빌드·스토어 업로드용 내부 도구입니다' },
    ],
  },
  {
    key: 'collab',
    title: '📝 문서 · 협업',
    hint: '기획은 노션, 작업은 GitHub Issues, 대화는 슬랙에서 합니다',
    links: [
      { label: 'Notion — R&D', url: 'https://app.notion.com/p/ndmarket/RND-ed8b80425a3a4572bcd967dd45b4ae22', essential: true, note: '기획·정책·회의록이 있습니다. 개발 정본은 저장소 문서입니다', access: '노션 워크스페이스 초대' },
      { label: '지식 베이스', url: 'https://nd-market.github.io/knowledge-base/', note: '도메인 문서입니다. 저장소 문서를 모아 증류한 사내 정본입니다' },
      { label: 'HCM', url: 'https://hcm.ndmarket.co.kr', note: '근태·인사를 관리합니다' },
    ],
  },
];
